import React, { useState, useEffect } from 'react';
import { apiService } from '../services/api';
import { Users, MapPin, Award, ArrowLeft, User, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const getImageUrl = (url) => {
  if (typeof url !== 'string' || !url.trim()) return '';
  const safeUrl = url.trim();
  if (safeUrl.startsWith('http://') || safeUrl.startsWith('https://')) return safeUrl;
  if (safeUrl.startsWith('/uploads/')) return safeUrl;

  const normalizedPath = safeUrl.startsWith('/') ? safeUrl : `/${safeUrl}`;
  const baseUrl = (BACKEND_URL || '').replace(/\/api\/?$/, '');
  if (!baseUrl) return normalizedPath;
  return `${baseUrl}${normalizedPath}`;
};

const TeamLogo = ({ logo, name, size = 'w-24 h-24' }) => {
  const [imageError, setImageError] = useState(false);
  const imageUrl = getImageUrl(logo);
  const initials = (name || '?')
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word.charAt(0).toUpperCase())
    .join('') || '?';

  if (imageUrl && !imageError) {
    return (
      <img
        src={imageUrl}
        alt={name}
        className={`${size} rounded-full border-4 border-white shadow-lg object-cover bg-white`}
        onError={() => setImageError(true)}
      />
    );
  }

  return (
    <div className={`${size} rounded-full border-4 border-white shadow-lg bg-white/95 text-green-700 font-bold flex items-center justify-center text-xl`}>
      {initials}
    </div>
  );
};

// Formation Field Component
const FormationField = ({ formation, players }) => {
  // Parse formation string (e.g., "4-3-3" -> [4, 3, 3])
  const parseFormation = (formationStr) => {
    if (!formationStr) return [4, 3, 3];
    const parts = formationStr.split('-').map(num => parseInt(num) || 0);
    return parts;
  };

  const formationParts = parseFormation(formation);
  
  // Group players by position
  const goalkeepers = players.filter(p => p.position === 'GK');
  const defenders = players.filter(p => p.position === 'DEF');
  const midfielders = players.filter(p => p.position === 'MID');
  const forwards = players.filter(p => p.position === 'FWD');

  // Get players for each line based on formation
  const getLineupPlayers = () => {
    const lines = [];
    
    // Always add goalkeeper line
    lines.push({ players: goalkeepers.slice(0, 1), position: 'GK' });
    
    // Add defense, midfield, forward based on formation parts
    if (formationParts.length >= 1) {
      lines.push({ players: defenders.slice(0, formationParts[0]), position: 'DEF' });
    }
    if (formationParts.length >= 2) {
      lines.push({ players: midfielders.slice(0, formationParts[1]), position: 'MID' });
    }
    if (formationParts.length >= 3) {
      lines.push({ players: forwards.slice(0, formationParts[2]), position: 'FWD' });
    }
    
    return lines.reverse(); // Reverse so forwards are at top
  };

  const lineup = getLineupPlayers();

  const getPositionColor = (position) => {
    switch (position) {
      case 'GK': return 'bg-yellow-500';
      case 'DEF': return 'bg-blue-500';
      case 'MID': return 'bg-green-500';
      case 'FWD': return 'bg-red-500';
      default: return 'bg-gray-500';
    }
  };

  return (
    <div className="relative w-full aspect-[3/4] max-w-lg mx-auto">
      {/* Soccer Field Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-green-600 to-green-700 rounded-lg overflow-hidden">
        {/* Field markings */}
        <div className="absolute inset-0">
          {/* Center circle */}
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-24 h-24 border-2 border-white/40 rounded-full" />
          {/* Center line */}
          <div className="absolute top-1/2 left-0 right-0 h-0.5 bg-white/40" />
          {/* Center dot */}
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-2 h-2 bg-white/60 rounded-full" />
          
          {/* Top penalty area */}
          <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-48 h-20 border-b-2 border-l-2 border-r-2 border-white/40" />
          <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-24 h-8 border-b-2 border-l-2 border-r-2 border-white/40" />
          
          {/* Bottom penalty area */}
          <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-48 h-20 border-t-2 border-l-2 border-r-2 border-white/40" />
          <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-24 h-8 border-t-2 border-l-2 border-r-2 border-white/40" />
          
          {/* Corner arcs */}
          <div className="absolute top-0 left-0 w-6 h-6 border-r-2 border-b-2 border-white/40 rounded-br-full" />
          <div className="absolute top-0 right-0 w-6 h-6 border-l-2 border-b-2 border-white/40 rounded-bl-full" />
          <div className="absolute bottom-0 left-0 w-6 h-6 border-r-2 border-t-2 border-white/40 rounded-tr-full" />
          <div className="absolute bottom-0 right-0 w-6 h-6 border-l-2 border-t-2 border-white/40 rounded-tl-full" />
        </div>

        {/* Players */}
        <div className="absolute inset-0 flex flex-col justify-between py-6 px-4">
          {lineup.map((line, lineIndex) => (
            <div 
              key={lineIndex} 
              className="flex justify-center items-center gap-2"
              style={{ flex: 1 }}
            >
              {line.players.map((player, playerIndex) => (
                <div 
                  key={playerIndex}
                  className="flex flex-col items-center group"
                  style={{
                    width: `${Math.min(80, 280 / Math.max(line.players.length, 1))}px`
                  }}
                >
                  <div className={`w-10 h-10 md:w-12 md:h-12 ${getPositionColor(line.position)} rounded-full flex items-center justify-center text-white font-bold shadow-lg border-2 border-white group-hover:scale-110 transition-transform`}>
                    {player.number || '?'}
                  </div>
                  <span className="text-white text-xs mt-1 text-center font-medium drop-shadow-lg truncate max-w-full">
                    {player.name?.split(' ')[0] || 'Pemain'}
                  </span>
                </div>
              ))}
              {line.players.length === 0 && (
                <div className="flex flex-col items-center opacity-50">
                  <div className={`w-10 h-10 ${getPositionColor(line.position)} rounded-full flex items-center justify-center text-white/50 border-2 border-white/30 border-dashed`}>
                    <User className="w-5 h-5" />
                  </div>
                  <span className="text-white/50 text-xs mt-1">{line.position}</span>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Formation label */}
      <div className="absolute top-2 right-2 bg-black/50 text-white px-3 py-1 rounded-full text-sm font-bold">
        {formation || '4-3-3'}
      </div>
    </div>
  );
};

// Team Detail Component
const TeamDetail = ({ team, onBack }) => {
  const players = team.players || [];
  
  const goalkeepers = players.filter(p => p.position === 'GK');
  const defenders = players.filter(p => p.position === 'DEF');
  const midfielders = players.filter(p => p.position === 'MID');
  const forwards = players.filter(p => p.position === 'FWD');

  const PlayerRow = ({ player }) => (
    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
      <div className="flex items-center space-x-3">
        {player.photo ? (
          <img src={player.photo} alt={player.name} className="w-10 h-10 rounded-full object-cover" />
        ) : (
          <div className="w-10 h-10 rounded-full bg-green-200 flex items-center justify-center">
            <span className="text-green-700 font-bold">{player.name?.charAt(0)}</span>
          </div>
        )}
        <span className="font-medium text-gray-900">{player.name}</span>
      </div>
      <span className="bg-green-600 text-white px-3 py-1 rounded-full text-sm font-bold">
        #{player.number}
      </span>
    </div>
  );

  const PositionSection = ({ title, icon, players, color }) => (
    <div className="mb-6">
      <div className={`flex items-center space-x-2 mb-3 ${color}`}>
        {icon}
        <h4 className="text-lg font-bold">{title}</h4>
        <span className="bg-gray-200 text-gray-700 px-2 py-0.5 rounded-full text-sm">
          {players.length}
        </span>
      </div>
      <div className="space-y-2">
        {players.map((player, index) => (
          <PlayerRow key={index} player={player} />
        ))}
        {players.length === 0 && (
          <p className="text-gray-500 text-sm italic">Belum ada pemain</p>
        )}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="container mx-auto px-4">
        {/* Back Button */}
        <button
          onClick={onBack}
          className="flex items-center space-x-2 text-green-600 hover:text-green-700 mb-6 transition-colors"
        >
          <ArrowLeft className="h-5 w-5" />
          <span className="font-medium">Kembali ke Daftar Tim</span>
        </button>

        {/* Team Header */}
        <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-lg p-8 mb-8 text-white">
          <div className="flex flex-col md:flex-row items-center md:items-start gap-6">
            <TeamLogo logo={team.logo} name={team.name} size="w-32 h-32" />
            <div className="text-center md:text-left">
              <h1 className="text-3xl font-bold mb-2">{team.name}</h1>
              <div className="flex flex-wrap justify-center md:justify-start gap-4 text-green-100">
                <div className="flex items-center space-x-2">
                  <MapPin className="h-5 w-5" />
                  <span>{team.city}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Award className="h-5 w-5" />
                  <span>{team.category}</span>
                </div>
              </div>
              <div className="mt-4 grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-green-200">Pelatih:</span>
                  <p className="font-semibold">{team.coach}</p>
                </div>
                {team.assistant_coach && (
                  <div>
                    <span className="text-green-200">Asisten:</span>
                    <p className="font-semibold">{team.assistant_coach}</p>
                  </div>
                )}
                <div>
                  <span className="text-green-200">Didirikan:</span>
                  <p className="font-semibold">{team.founded_year || team.foundedYear}</p>
                </div>
                <div>
                  <span className="text-green-200">Formasi:</span>
                  <p className="font-semibold">{team.formation || '4-3-3'}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Formation Visualization */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Formasi Tim</h3>
            <FormationField formation={team.formation} players={players} />
            
            {/* Legend */}
            <div className="mt-4 flex flex-wrap justify-center gap-4 text-sm">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-yellow-500 rounded-full"></div>
                <span>Kiper</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-blue-500 rounded-full"></div>
                <span>Belakang</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-green-500 rounded-full"></div>
                <span>Tengah</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-red-500 rounded-full"></div>
                <span>Depan</span>
              </div>
            </div>
          </div>

          {/* Squad List */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Daftar Pemain</h3>
            
            <PositionSection 
              title="Kiper" 
              icon={<div className="w-4 h-4 bg-yellow-500 rounded-full"></div>}
              players={goalkeepers}
              color="text-yellow-600"
            />
            
            <PositionSection 
              title="Pemain Belakang" 
              icon={<div className="w-4 h-4 bg-blue-500 rounded-full"></div>}
              players={defenders}
              color="text-blue-600"
            />
            
            <PositionSection 
              title="Pemain Tengah" 
              icon={<div className="w-4 h-4 bg-green-500 rounded-full"></div>}
              players={midfielders}
              color="text-green-600"
            />
            
            <PositionSection 
              title="Pemain Depan" 
              icon={<div className="w-4 h-4 bg-red-500 rounded-full"></div>}
              players={forwards}
              color="text-red-600"
            />

            {players.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                <Users className="h-12 w-12 mx-auto mb-2 opacity-50" />
                <p>Belum ada data pemain</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Main Peserta Component
const normalizeSchoolName = (teamName = '') => {
  const baseName = String(teamName)
    .replace(/\s+KU\s*\d+$/i, '')
    .replace(/\s+(BLUE|RED|GREEN|YELLOW|WHITE|BLACK)$/i, '')
    .replace(/\s+[ABC]$/i, '')
    .trim();
  return baseName || String(teamName).trim();
};

const isSchoolLike = (team = {}) => {
  const explicit = (team.participant_type || '').toLowerCase();
  if (explicit === 'school') return true;
  if (explicit === 'club') return false;

  const name = String(team.name || '').toLowerCase().trim();
  return /^sd\b|^sdn\b|^sdit\b|^mi\b|^min\b|^mts\b|^smp\b|^sma\b|^smk\b|^tk\b/.test(name) ||
    /school/.test(name);
};

const Peserta = ({ participantType = 'school' }) => {
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [participants, setParticipants] = useState([]);
  const [selectedSchool, setSelectedSchool] = useState(null);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadParticipants();
  }, [participantType]);

  const loadParticipants = async () => {
    try {
      const response = await apiService.getParticipants({
        target: participantType === 'club' ? 'club' : 'school',
      });
      const data = response.data || [];
      const filtered = data.filter((team) => (participantType === 'club' ? !isSchoolLike(team) : isSchoolLike(team)));
      setParticipants(filtered);
    } catch (error) {
      console.error('Error loading participants:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredParticipants = participants.filter((team) => {
    const categoryMatch = categoryFilter === 'all' || team.category === categoryFilter;
    const searchMatch = team.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                       team.city?.toLowerCase().includes(searchTerm.toLowerCase());
    return categoryMatch && searchMatch;
  });

  const schoolMap = filteredParticipants.reduce((acc, team) => {
    const schoolName = normalizeSchoolName(team.name);
    if (!acc[schoolName]) {
      acc[schoolName] = {
        schoolName,
        city: team.city,
        teams: [],
      };
    }
    acc[schoolName].teams.push(team);
    return acc;
  }, {});

  const filteredSchools = Object.values(schoolMap)
    .sort((a, b) => a.schoolName.localeCompare(b.schoolName, 'id'));

  const schoolTeams = selectedSchool
    ? filteredParticipants
        .filter((team) => normalizeSchoolName(team.name) === selectedSchool)
        .sort((a, b) => a.name.localeCompare(b.name, 'id'))
    : [];

  const availableCategories = Array.from(new Set(participants.map((team) => team.category).filter(Boolean)))
    .sort((a, b) => a.localeCompare(b, 'id'));

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-green-600" />
      </div>
    );
  }

  if (selectedTeam) {
    return <TeamDetail team={selectedTeam} onBack={() => setSelectedTeam(null)} />;
  }

  const title = participantType === 'club' ? 'Peserta Klub' : 'Peserta Sekolah';

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="container mx-auto px-4">
        <div className="flex items-center space-x-3 mb-8">
          <Users className="h-10 w-10 text-green-600" />
          <h1 className="text-4xl font-bold text-gray-900">{title}</h1>
        </div>

        {participantType === 'school' && selectedSchool && (
          <button
            onClick={() => setSelectedSchool(null)}
            className="mb-6 inline-flex items-center gap-2 text-green-700 hover:text-green-800 font-medium"
          >
            <ArrowLeft className="h-4 w-4" />
            Kembali ke daftar sekolah
          </button>
        )}

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Kategori
              </label>
              <select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent"
              >
                <option value="all">Semua Kategori</option>
                {availableCategories.map((category) => (
                  <option key={category} value={category}>{category}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {participantType === 'school' && selectedSchool ? 'Cari Tim' : 'Cari Sekolah / Tim'}
              </label>
              <input
                type="text"
                placeholder={participantType === 'school' && selectedSchool ? 'Cari nama tim...' : 'Cari sekolah, nama tim, atau kota...'}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent"
              />
            </div>
          </div>
        </div>

        {participantType === 'club' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredParticipants
              .sort((a, b) => a.name.localeCompare(b.name, 'id'))
              .map((team) => (
                <div
                  key={team.id}
                  onClick={() => setSelectedTeam(team)}
                  className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-xl transition-all duration-200 transform hover:-translate-y-1 cursor-pointer"
                >
                  <div className="bg-gradient-to-br from-green-500 to-green-600 p-6 text-white">
                    <div className="flex items-center justify-center mb-4">
                      <TeamLogo logo={team.logo} name={team.name} />
                    </div>
                    <h3 className="text-xl font-bold text-center mb-2">{team.name}</h3>
                    <div className="flex items-center justify-center space-x-2 text-green-100">
                      <MapPin className="h-4 w-4" />
                      <span className="text-sm">{team.city || '-'}</span>
                    </div>
                  </div>
                  <div className="p-6 space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">Kategori:</span>
                      <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-semibold">
                        {team.category}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">Pelatih:</span>
                      <span className="font-semibold text-gray-900 text-right">{team.coach}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">Jumlah Pemain:</span>
                      <span className="font-semibold text-gray-900">{team.players?.length || 0}</span>
                    </div>
                    <button className="w-full mt-2 bg-green-600 text-white py-2 rounded-lg hover:bg-green-700 transition-colors font-medium">
                      Lihat Detail Tim
                    </button>
                  </div>
                </div>
              ))}
          </div>
        ) : !selectedSchool ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredSchools.map((school) => {
              const totalPlayers = school.teams.reduce((sum, team) => sum + (team.players?.length || 0), 0);
              const schoolLogo = school.teams[0]?.logo;
              return (
                <div
                  key={school.schoolName}
                  onClick={() => setSelectedSchool(school.schoolName)}
                  className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-xl transition-all duration-200 transform hover:-translate-y-1 cursor-pointer"
                >
                  <div className="bg-gradient-to-br from-green-500 to-green-600 p-6 text-white">
                    <div className="flex items-center justify-center mb-4">
                      <TeamLogo logo={schoolLogo} name={school.schoolName} />
                    </div>
                    <h3 className="text-xl font-bold text-center mb-2">{school.schoolName}</h3>
                    <div className="flex items-center justify-center space-x-2 text-green-100">
                      <MapPin className="h-4 w-4" />
                      <span className="text-sm">{school.city || '-'}</span>
                    </div>
                  </div>
                  <div className="p-6 space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">Jumlah Tim:</span>
                      <span className="font-semibold text-gray-900">{school.teams.length}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">Total Pemain:</span>
                      <span className="font-semibold text-gray-900">{totalPlayers}</span>
                    </div>
                    <button className="w-full mt-2 bg-green-600 text-white py-2 rounded-lg hover:bg-green-700 transition-colors font-medium">
                      Lihat Tim Terdaftar
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-5">Tim Terdaftar - {selectedSchool}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {schoolTeams.map((team) => (
                <div
                  key={team.id}
                  onClick={() => setSelectedTeam(team)}
                  className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-xl transition-all duration-200 transform hover:-translate-y-1 cursor-pointer"
                >
                  <div className="bg-gradient-to-br from-green-500 to-green-600 p-6 text-white">
                    <div className="flex items-center justify-center mb-4">
                      <TeamLogo logo={team.logo} name={team.name} />
                    </div>
                    <h3 className="text-xl font-bold text-center mb-2">{team.name}</h3>
                  </div>
                  <div className="p-6 space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">Kategori:</span>
                      <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-semibold">
                        {team.category}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">Pelatih:</span>
                      <span className="font-semibold text-gray-900 text-right">{team.coach}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">Jumlah Pemain:</span>
                      <span className="font-semibold text-gray-900">{team.players?.length || 0}</span>
                    </div>
                    <button className="w-full mt-2 bg-green-600 text-white py-2 rounded-lg hover:bg-green-700 transition-colors font-medium">
                      Lihat Detail Tim
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Statistics */}
        <div className="mt-12 bg-white rounded-lg shadow-md p-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Statistik Peserta</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="bg-green-100 w-16 h-16 rounded-full mx-auto mb-3 flex items-center justify-center">
                <Users className="h-8 w-8 text-green-600" />
              </div>
              <p className="text-3xl font-bold text-gray-900">{Object.keys(participants.reduce((acc, team) => { acc[normalizeSchoolName(team.name)] = true; return acc; }, {})).length}</p>
              <p className="text-gray-600 mt-1">Total Sekolah</p>
            </div>
            <div className="text-center">
              <div className="bg-blue-100 w-16 h-16 rounded-full mx-auto mb-3 flex items-center justify-center">
                <Award className="h-8 w-8 text-blue-600" />
              </div>
              <p className="text-3xl font-bold text-gray-900">{participants.length}</p>
              <p className="text-gray-600 mt-1">Total Tim</p>
            </div>
            <div className="text-center">
              <div className="bg-purple-100 w-16 h-16 rounded-full mx-auto mb-3 flex items-center justify-center">
                <Award className="h-8 w-8 text-purple-600" />
              </div>
              <p className="text-3xl font-bold text-gray-900">{participants.reduce((sum, team) => sum + (team.players?.length || 0), 0)}</p>
              <p className="text-gray-600 mt-1">Total Pemain</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Peserta;
